/**
 * The capture host (1.N-1 deliverable 16): Mod-Shift-K from any page opens a one-line capture;
 * Enter saves it to the Inbox without leaving what the GM was writing. Mounted once by the frame.
 */
import { flagsFromMe, inboxCapture, tid } from '@codex/contracts';
import { Button, Dialog, isApplePlatform, TextField } from '@codex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { api } from '../auth/client.js';
import { useNotify } from '../shell/notices.js';
import { useShellSession } from '../shell/session.js';
import {
  CAPTURE_FAILED,
  CAPTURE_HINT,
  CAPTURE_MAX,
  CAPTURE_SAVED,
  captureText,
  isCaptureKey,
} from './capture-model.js';
import {
  closeCapture,
  getCapture,
  openCapture,
  setDraft,
  subscribeCapture,
} from './capture-store.js';

/** Opens the capture dialog; needs no provider. */
export function useCapture(): () => void {
  return openCapture;
}

export function CaptureHost(): React.JSX.Element | null {
  const me = useShellSession();
  const enabled = me !== null && flagsFromMe(me).catalogue;
  useEffect(() => {
    if (!enabled) return;
    const apple = isApplePlatform();
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !isCaptureKey(event, apple)) return;
      event.preventDefault();
      openCapture();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
  return enabled ? <CaptureDialog /> : null;
}

function CaptureDialog(): React.JSX.Element {
  const { open, draft, focusTick } = useSyncExternalStore(subscribeCapture, getCapture);
  const client = useQueryClient();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: focusTick re-focuses the input
  useEffect(() => {
    if (!open) {
      setFailed(false);
      return;
    }
    document.querySelector<HTMLInputElement>('input[data-testid="inbox.capture.input"]')?.focus();
  }, [open, focusTick]);

  const save = async () => {
    const text = captureText(draft);
    if (text === null || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await api(inboxCapture, { body: { text } });
    } catch {
      setFailed(true);
      setBusy(false);
      return;
    }
    setBusy(false);
    setDraft('');
    closeCapture();
    notify(CAPTURE_SAVED);
    void client.invalidateQueries({ queryKey: ['inbox'] });
  };

  return (
    <Dialog
      open={open}
      title="Capture to the Inbox"
      description={CAPTURE_HINT}
      busy={busy}
      onClose={closeCapture}
      actions={
        <>
          <Button onClick={closeCapture} disabled={busy}>
            Close
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={captureText(draft) === null}
            onClick={() => void save()}
          >
            Save
          </Button>
        </>
      }
    >
      <TextField
        label="Note"
        maxLength={CAPTURE_MAX}
        value={draft}
        autoComplete="off"
        {...tid('inbox.capture.input')}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
          event.preventDefault();
          void save();
        }}
      />
      {failed && <p role="alert">{CAPTURE_FAILED}</p>}
    </Dialog>
  );
}
